import {
  type CSSProperties,
  type HTMLAttributes,
  useEffect,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import styles from './AnimatePresence.module.scss'

type AxisValue = number | string

type MotionState = {
  x?: AxisValue
  y?: AxisValue
  opacity?: number
}

type MotionConfig = {
  initial?: MotionState
  animate?: MotionState
  exit?: MotionState
  transition?: {
    duration?: number // seconds
    easing?: string
  }
}

type PresenceProps = {
  open: boolean
  motion: MotionConfig
  children: React.ReactNode
  onExitComplete?: () => void
} & HTMLAttributes<HTMLDivElement>

// Helper to build transform string
function buildTransform({ x, y }: MotionState) {
  const tx = x !== undefined ? `translateX(${x})` : ''
  const ty = y !== undefined ? `translateY(${y})` : ''

  return [tx, ty].filter(Boolean).join(' ')
}

// Helper to apply styles imperatively
function applyStyles(el: HTMLElement, state: MotionState) {
  el.style.transform = buildTransform(state)
  if (state.opacity !== undefined) el.style.opacity = String(state.opacity)
}

const AnimatePresence = ({
  open,
  motion,
  children,
  onExitComplete,
  className
}: PresenceProps) => {
  const [mounted, setMounted] = useState(open)
  const ref = useRef<HTMLDivElement>(null)

  const { initial = {}, animate = {}, exit = {}, transition = {} } = motion

  const duration = transition.duration ?? 0.3
  const easing = transition.easing ?? 'ease'

  // Mount when opening
  useEffect(() => {
    if (open) setMounted(true)
  }, [open])

  // Apply animations
  useEffect(() => {
    const el = ref.current

    if (!el) return

    const target = open ? animate : exit

    requestAnimationFrame(() => applyStyles(el, target))

    let timeout: ReturnType<typeof setTimeout> | null = null

    if (!open) {
      timeout = setTimeout(() => {
        setMounted(false)
        onExitComplete?.()
      }, duration * 1000)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [open, animate, exit, duration, onExitComplete])

  if (!mounted) return null

  return (
    <div
      ref={ref}
      className={clsx(styles.motion, className)}
      style={
        {
          '--presence-x': initial.x ?? 0,
          '--presence-y': initial.y ?? 0,
          '--presence-opacity': initial.opacity ?? 1,
          '--presence-duration': `${duration}s`,
          '--presence-easing': easing
        } as CSSProperties
      }
    >
      {children}
    </div>
  )
}

export default AnimatePresence
